// frontend/src/components/TextHighlighter/TextHighlighter.jsx
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next'; 
import { Tooltip, Box, IconButton, CircularProgress } from '@mui/material';
import GTranslateIcon from '@mui/icons-material/GTranslate';
// Import the custom translation hook (verify your relative path matches your directory setup)
import { useDynamicTranslation } from '../HeatmapModal/useDynamicTranslation'; 
import './TextHighlighter.css';

const getHighlightStylesFromSeverity = (severity) => {
  const ratio = (Math.max(1, Math.min(10, severity)) - 1) / 9;
  const startColor = { h: 215, s: 58, l: 91 };
  const endColor = { h: 210, s: 28, l: 25 };
  const lightness = startColor.l - (startColor.l - endColor.l) * ratio;
  const saturation = startColor.s - (startColor.s - endColor.s) * ratio;
  const backgroundColor = `hsla(${startColor.h}, ${saturation}%, ${lightness}%, 0.85)`;
  const textColor = lightness < 65 ? '#f0f0f0' : '#333';
  return { backgroundColor, color: textColor };
};

// --- SUB-COMPONENT FOR DYNAMIC TOOLTIP TRANSLATION ---
const HighlighterTooltipContent = ({ reason, severity }) => {
  const { t, i18n } = useTranslation();
  const { 
    textToShow,
    isTranslating,
    isCheckingCache,
    finalTranslation,
    handleTranslate 
  } = useDynamicTranslation(reason);

  // Only show the translate control if the current language is not English and it's not already translated
  const shouldShowTranslateControl = i18n.language !== 'en' && !finalTranslation;

  return (
    <div className="custom-tooltip-content">
      <div className="tooltip-header">
        {shouldShowTranslateControl && (
          <Tooltip title={t('textHighlighter.tooltip.translateReason') || t('heatmap.tooltip.translateReason')} placement="top">
            <span>
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation(); // Avoid triggering click events on parent text components
                  handleTranslate();
                }} 
                disabled={isCheckingCache || isTranslating}
                className="translate-tooltip-button"
              >
                {isCheckingCache || isTranslating ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <GTranslateIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}
        <span>{t('textHighlighter.tooltip.severity')}: {severity}/10</span>
      </div>
      
      {isTranslating ? (
        <div className="tooltip-reason-loader with-message">
          <p style={{ margin: 0, fontSize: '0.8rem' }}>{t('resultDisplay.translatingMessage') || 'Translating...'}</p>
        </div>
      ) : (
        <p className="tooltip-reason">{textToShow}</p>
      )}
    </div>
  );
};

const TextHighlighter = ({ text, regions }) => {
  const { t } = useTranslation();
  // --- CHANGE #1: RENAME STATE FOR CLARITY ---
  // This now tracks if ANY highlight has focus.
  const [hasFocus, setHasFocus] = useState(false);

  const segments = useMemo(() => {
    if (!regions || regions.length === 0 || !text) {
      return [<p key="full-text">{text}</p>];
    }
    const validSpans = regions
      .map(region => {
        const { quote, reason, severity } = region;
        if (!quote) return null;
        const startIndex = text.indexOf(quote);
        if (startIndex === -1) {
          console.warn("Could not locate quote in text:", `"${quote}"`);
          return null;
        }
        return {
          start: startIndex,
          end: startIndex + quote.length,
          reason,
          severity: severity || 5,
        };
      })
      .filter(Boolean);
    const sortedSpans = validSpans.sort((a, b) => a.start - b.start);
    const jsxSegments = [];
    let lastIndex = 0;
    sortedSpans.forEach((span, index) => {
      if (span.start > lastIndex) {
        jsxSegments.push(
          <span key={`text-${lastIndex}`}>{text.substring(lastIndex, span.start)}</span>
        );
      }
      const highlightStyle = getHighlightStylesFromSeverity(span.severity);
      jsxSegments.push(
        <Tooltip
          key={`region-${index}`}
          title={<HighlighterTooltipContent reason={span.reason} severity={span.severity} />}
          arrow
          interactive
          leaveDelay={300}
          placement="top"
          PopperProps={{
            className: 'text-highlighter-tooltip-popper'
          }}
        >
          <span
            className="highlighted-text"
            style={highlightStyle}
            onMouseEnter={() => setHasFocus(true)}
            onMouseLeave={() => setHasFocus(false)}
          >
            {text.substring(span.start, span.end)}
          </span>
        </Tooltip>
      );
      lastIndex = span.end;
    }, [text, regions, t]);
    if (lastIndex < text.length) {
      jsxSegments.push(<span key={`text-${lastIndex}`}>{text.substring(lastIndex)}</span>);
    }
    return jsxSegments;
  }, [text, regions, t]);

  const containerClassName = `text-highlight-paragraph ${hasFocus ? 'has-hover-focus' : ''}`;

  return (
    <Box className={containerClassName}>
      {segments.map((segment, index) => (
        <React.Fragment key={index}>{segment}</React.Fragment>
      ))}
    </Box>
  );
};

export default TextHighlighter;